import { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';

const TYPES = {
  warning: {
    button: 'bg-red-600 hover:bg-red-500 focus-visible:outline-red-600',
    iconBackground: 'bg-red-100',
  },
  default: {
    button: 'bg-emerald-600 hover:bg-emerald-500 focus-visible:outline-emerald-600',
    iconBackground: 'bg-green-100',
  },
};

export default function ConfirmationDialog({
  open,
  handleCancel,
  handleConfirm,
  title,
  message,
  confirmText,
  cancelText,
  icon,
  type,
}) {
  const cancelButtonRef = useRef(null);

  const getIconBackground = () => {
    return TYPES[type]?.iconBackground || TYPES['default'].iconBackground;
  };

  const getButtonBackground = () => {
    return TYPES[type]?.button || TYPES['default'].button;
  };

  const concatenateClasses = (classes) => {
    return classes.join(' ');
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-[60]'
        initialFocus={cancelButtonRef}
        onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-full justify-center p-4 text-center items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'>
              <Dialog.Panel className='relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6'>
                <div>
                  <div
                    className={concatenateClasses([
                      'mx-auto flex h-12 w-12 items-center justify-center rounded-full',
                      getIconBackground(type),
                    ])}>
                    {icon}
                  </div>
                  <div className='mt-3 text-center sm:mt-5'>
                    <Dialog.Title
                      as='h3'
                      className='text-base font-semibold leading-6 text-gray-900'>
                      {title}
                    </Dialog.Title>
                    <div className='mt-2'>
                      <p className='text-sm text-gray-500'>{message}</p>
                    </div>
                  </div>
                </div>
                <div className='mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3'>
                  <button
                    type='button'
                    className={concatenateClasses([
                      'inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:col-start-2',
                      getButtonBackground(type),
                    ])}
                    onClick={handleConfirm}>
                    {confirmText}
                  </button>
                  <button
                    type='button'
                    className='mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0'
                    onClick={handleCancel}
                    ref={cancelButtonRef}>
                    {cancelText}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
